import axios from 'axios';

import {
  AuditCarrierDetail,
  AuditCarrierSummary,
  Rate,
  RateFilter,
  transformRateForAPI,
  transformRateFromAPI,
} from '../models/Rate';

const uri = '/rate';
const RATE_PAGE_LIMIT = 500;

interface RatePageResponse {
  data: Rate[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

export type RateSearchOptions = {
  limit?: number;
  offset?: number;
  /** when set, fetch all rates for that carrier via GET /carrier/:id/rate (unfiltered) */
  carrierId?: number;
};

const buildSearchParams = (
  filter?: RateFilter | null,
  options: RateSearchOptions = {}
) => {
  const params: Record<string, string | number> = {};

  if (options.limit != null) {
    params.limit = options.limit;
  }
  if (options.offset != null) {
    params.offset = options.offset;
  }
  if (filter?.carrierIds?.length) {
    params.carrierIds = filter.carrierIds.join(',');
  }
  if (filter?.modeNames?.length) {
    params.modeNames = filter.modeNames.join(',');
  }
  if (filter?.origin) {
    params.origin = filter.origin;
  }
  if (filter?.destination) {
    params.destination = filter.destination;
  }
  if (filter?.searchType) {
    params.searchType = filter.searchType;
  }

  return params;
};

/**
 * Rate search. Carrier-scoped fetches return the full unfiltered set for
 * client-side filtering; admin (no carrierId) forwards filter params to GET /rate.
 */
const search = async (
  filter?: RateFilter | null,
  options: RateSearchOptions = {}
): Promise<Rate[]> => {
  if (options.carrierId) {
    const { data } = await axios.get<Rate[]>(
      `/carrier/${options.carrierId}/rate`
    );
    return data.map(transformRateFromAPI);
  }

  const params = buildSearchParams(filter, {
    ...options,
    limit: options.limit ?? RATE_PAGE_LIMIT,
    offset: options.offset ?? 0,
  });

  const { data } = await axios.get<RatePageResponse>(uri, { params });
  return data.data.map(transformRateFromAPI);
};

const create = async (rate: Rate) => {
  const transformedRate = transformRateForAPI(rate);
  const { data } = await axios.post<Rate>(`${uri}`, transformedRate);
  return transformRateFromAPI(data);
};

const getAll = async (carrierId?: number, filter?: RateFilter | null) => {
  return search(filter, { carrierId });
};

const update = async (rate: Rate) => {
  const transformedRate = transformRateForAPI(rate);
  const { data } = await axios.put<Rate>(`${uri}/${rate.id}`, transformedRate);
  return transformRateFromAPI(data);
};

const bulkAdd = async (rates: Rate[]) => {
  const transformedRates = rates.map(transformRateForAPI);
  const { data } = await axios.post<Rate[]>(
    `${uri}/bulk/add`,
    transformedRates
  );
  return data.map(transformRateFromAPI);
};

const bulkRemove = async (ids: number[]) => {
  await axios.post(`${uri}/bulk/remove`, ids);
};

const bulkReverse = async (ids: number[]) => {
  const { data } = await axios.post<Rate[]>(`${uri}/bulk/reverse`, ids);
  return data.map(transformRateFromAPI);
};

const bulkDuplicate = async (ids: number[]) => {
  const { data } = await axios.post<Rate[]>(`${uri}/bulk/duplicate`, ids);
  return data.map(transformRateFromAPI);
};

const bulkUpdate = async (rates: Rate[]) => {
  const transformedRates = rates.map(transformRateForAPI);
  const { data } = await axios.post<Rate[]>(
    `${uri}/bulk/update`,
    transformedRates
  );
  return data.map(transformRateFromAPI);
};

const bulkUpdatePreferred = async (ids: number[], preferred: number) => {
  const { data } = await axios.post(`${uri}/bulk/update/preferred`, {
    preferred,
    rates: ids,
  });
  return data;
};

const assignTerminals = async (
  ids: number[],
  terminals: {
    originTerminals?: number[];
    destinationTerminals?: number[];
  }
) => {
  const { data } = await axios.post<Rate[]>(`${uri}/terminal`, {
    rates: ids,
    ...terminals,
  });
  return data.map(transformRateFromAPI);
};

const singleAssignTerminals = async (
  id: number,
  originTerminals: number[],
  destinationTerminals: number[]
) => {
  const { data } = await axios.post<Rate>(`${uri}/terminal/single`, {
    destinationTerminals,
    id,
    originTerminals,
  });
  return transformRateFromAPI(data);
};

const adjustBaseRates = async (
  ids: number[],
  adjustBy: number,
  adjustType: string,
  note?: string
) => {
  const { data } = await axios.post<Rate[]>(`${uri}/bulk/baseadjust`, {
    amount: adjustBy,
    note: note ?? undefined,
    rates: ids,
    type: adjustType,
  });
  return data.map(transformRateFromAPI);
};

const getAuditSummary = async (): Promise<AuditCarrierSummary[]> => {
  const { data } = await axios.get<AuditCarrierSummary[]>(`${uri}/audit`);
  return data;
};

const getAuditByCarrier = async (
  carrierId: number
): Promise<AuditCarrierDetail> => {
  const { data } = await axios.get<AuditCarrierDetail>(
    `${uri}/audit/${carrierId}`
  );
  // transform rates within each duplicate group to be Rate objects
  return {
    ...data,
    duplicateGroups: data.duplicateGroups.map((group) => ({
      ...group,
      rates: group.rates.map(transformRateFromAPI),
    })),
  };
};

const updateCoordinates = async (rateId: number) => {
  const { data } = await axios.post<{ rate: any }>(
    `${uri}/${rateId}/update-coordinates`
  );
  const rate = data.rate ?? data;
  return transformRateFromAPI(rate);
};

export interface BulkUpdateCoordinatesResult {
  total: number;
  updated: number;
  skipped: number;
  failed: number;
  results: Array<{ id: number; coordsUpdated: boolean }>;
  errors: Array<{ id: number; error: string }>;
  remainingIds: number[];
}

const bulkUpdateCoordinates = async (
  ids: number[]
): Promise<BulkUpdateCoordinatesResult> => {
  const { data } = await axios.post<BulkUpdateCoordinatesResult>(
    `${uri}/bulk/update-coordinates`,
    { ids }
  );
  return data;
};

export interface RateHistoryRateClass {
  classId: number;
  value: number;
  createdAt: string;
  updatedAt: string;
  isEstimate?: boolean;
  validatedAt?: string | null;
  estimateConfidence?: string | null;
}

export interface RateHistoryRecord {
  id: number;
  key: string;
  parent_id: number | null;
  value: string | null;
  user_id: number;
  metadataJson: {
    history?: {
      rate?: Record<string, unknown>;
      rateClasses?: RateHistoryRateClass[];
    };
  } | null;
  createdAt: string;
  updatedAt: string;
  User: { firstName: string };
}

const getHistory = async (rateId: number): Promise<RateHistoryRecord[]> => {
  const { data } = await axios.get<RateHistoryRecord[]>(
    `${uri}/${rateId}/history`
  );
  return data;
};

const hasHistory = async (rateId: number): Promise<boolean> => {
  const { data } = await axios.get<{ hasHistory: boolean }>(
    `${uri}/${rateId}/history/exists`
  );
  return data.hasHistory;
};

const mergeDuplicateRates = async (
  carrierId: number,
  origin: string,
  destination: string,
  shipTypeId: number,
  destinationShipTypeId: number,
  modeId: number,
  selectedRateClasses?: { [classId: number]: number },
  selectedRateId?: number | null
): Promise<Rate> => {
  const body: Record<string, unknown> = {
    carrierId,
    destination,
    destinationShipTypeId,
    modeId,
    origin,
    selectedRateClasses,
    shipTypeId,
  };
  if (selectedRateId != null) {
    body.selectedRateId = selectedRateId;
  }
  const { data } = await axios.post<Rate>(`${uri}/audit/merge`, body);
  return transformRateFromAPI(data);
};

const apis = {
  adjustBaseRates,
  assignTerminals,
  bulkAdd,
  bulkDuplicate,
  bulkRemove,
  bulkReverse,
  bulkUpdate,
  bulkUpdateCoordinates,
  bulkUpdatePreferred,
  create,
  getAll,
  getAuditByCarrier,
  getAuditSummary,
  getHistory,
  hasHistory,
  mergeDuplicateRates,
  search,
  singleAssignTerminals,
  update,
  updateCoordinates,
};

export default apis;
